import { useAdminSites } from '@ui/data/admin';
import { useGetSnapSiteConfigQuery } from '@ui/endpoints/get-snap-site-config';
import {
  snapSiteConfigActions,
  snapSiteConfigSelectors,
} from '@ui/features/snap-site-config/state';
import { useDispatch } from '@ui/hooks/useDispatch';
import { useSelector } from '@ui/hooks/useSelector';
import { NotFound } from '@ui/pages/NotFound';
import { isAxiosError } from 'axios';
import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router';

type WithCurrentSiteProps = {
  checkAccess?: boolean;
  children: React.ReactNode;
  fallback: React.ReactNode;
};

export function WithCurrentSite({ children, fallback }: WithCurrentSiteProps) {
  const dispatch = useDispatch();
  const sitesQuery = useAdminSites();

  const { siteCode } = useParams<{ siteCode: string }>();
  if (!siteCode) {
    throw new Error('Must have a site code.');
  }

  const previousSiteCodeRef = useRef<string | undefined>(undefined);

  const currentSite = useSelector(snapSiteConfigSelectors.selectCurrentSite);

  const allSites = sitesQuery.isSuccess ? sitesQuery.data : [];

  const selectedSite = allSites.find(
    (site) => siteCode.toLowerCase() === site.code.toLowerCase(),
  );

  if (allSites.length > 0 && !selectedSite) {
    throw new Error('Site not found');
  }

  const snapSiteConfigQuery = useGetSnapSiteConfigQuery(
    {
      siteCode: selectedSite?.code ?? '',
    },
    {
      skip: !selectedSite,
    },
  );

  useEffect(() => {
    if (!sitesQuery.isSuccess) {
      return;
    }

    // Clear config when site code changes (before new data loads)
    if (
      previousSiteCodeRef.current !== undefined &&
      previousSiteCodeRef.current !== siteCode
    ) {
      dispatch(snapSiteConfigActions.clearSnapSiteConfig());
    }
    previousSiteCodeRef.current = siteCode;

    if (snapSiteConfigQuery.isSuccess && snapSiteConfigQuery.data.site.code) {
      // Process the response to match the Store state
      const { site } = snapSiteConfigQuery.data;

      const siteConfigData = {
        ...site,
        currentTeam: null,
      };

      dispatch(snapSiteConfigActions.setSnapSiteConfig(siteConfigData));
    }
  }, [
    siteCode,
    sitesQuery.isSuccess,
    snapSiteConfigQuery.isSuccess,
    snapSiteConfigQuery.data,
    dispatch,
  ]);

  if (
    sitesQuery.isLoading ||
    (snapSiteConfigQuery.isLoading && !snapSiteConfigQuery.data)
  ) {
    return fallback;
  }

  if (snapSiteConfigQuery.isError) {
    const error = snapSiteConfigQuery.error;

    if (isAxiosError(error) && error?.response?.status === 404) {
      return <NotFound />;
    }

    throw new Error('Error fetching current site!');
  }

  if (!snapSiteConfigQuery.isSuccess || !currentSite.code) {
    // This is the render cycle between the query succeeding and the
    // dispatched setSnapSiteConfig action loading the data into the Redux store
    return fallback;
  }

  if (snapSiteConfigQuery.isSuccess) {
    return children;
  }

  return null;
}
